/**
 * URLの安全性をチェックする
 *
 * @param url チェック対象のURL
 * @returns 安全なURLならそのまま、危険なURLならnull
 */
const sanitizeUrl = (url: string): string | null => {
    const trimmedUrl = url.trim();

    // 空文字チェック
    if (trimmedUrl === "") {
        return null;
    }

    // 相対パスや絶対パス（スキームなし）のパターンをチェック
    // これらは安全なのでそのまま返す
    if (
        // 絶対パス: /path/to
        trimmedUrl.startsWith("/") ||
        // 相対パス: ./path/to
        trimmedUrl.startsWith("./") ||
        // 相対パス: ../path/to
        trimmedUrl.startsWith("../") ||
        // スキームなし
        !/^[A-Za-z][\d+.A-Za-z-]*:/u.test(trimmedUrl)) {

        return trimmedUrl;
    }

    // URLクラスでパース試行（絶対URLの場合）
    try {
        const parsedUrl = new URL(trimmedUrl);
        const protocol = parsedUrl.protocol;

        // http(s)のみ許可
        if (protocol === "http:" || protocol === "https:") {
            return trimmedUrl;
        }

        // それ以外のスキーム（javascript:, data:, vbscript: など）は拒否
        return null;
    } catch {
        // URLクラスでパースできなかった = 相対パスの可能性
        // スキームっぽいパターンがないかチェック
        if (/^[A-Za-z][\d+.A-Za-z-]*:/u.test(trimmedUrl)) {
            // スキームっぽいのに http(s) じゃない = 危険
            return null;
        }

        // スキームなしの相対パス
        return trimmedUrl;
    }
};

/**
 * プレーンテキストをフラグメントに追加する。
 * テキスト中の改行(\n)は <br> 要素に変換する。
 *
 * @param fragment 追加先のDocumentFragment
 * @param text 追加するプレーンテキスト
 */
const appendTextWithBreaks = (fragment: DocumentFragment, text: string): void => {
    const lines = text.split("\n");

    lines.forEach((line, index) => {
        if (line !== "") {
            fragment.append(document.createTextNode(line));
        }

        // 最後の行以外は改行だったので <br> に変換する
        if (index < lines.length - 1) {
            fragment.append(document.createElement("br"));
        }
    });
};

/**
 * お知らせテキストをパースし、DOM要素に変換して返す。
 *
 * @example
 * ```typescript
 * const text1 = "aaa";
 * const result1 = parseNewsText(text1);
 * // result1 => DocumentFragment("aaa")
 *
 * const text2 = "aaa[http://www.example.com]bbb";
 * const result2 = parseNewsText(text2);
 * // result2 => DocumentFragment(
 * //   "aaa",
 * //   <a href="http://www.example.com">http://www.example.com</a>,
 * //   "bbb")
 *
 * const text3 = "[aaa](http://www.example.com)bbb[ccc](http://www2.example.com)";
 * const result3 = parseNewsText(text3);
 * // result3 => DocumentFragment(
 * //   <a href="http://www.example.com">aaa</a>,
 * //   "bbb",
 * //   <a href="http://www2.example.com">ccc</a>)
 *
 * const text4 = "foo\nbar";
 * const result4 = parseNewsText(text4);
 * // result4 => DocumentFragment("foo", <br>, "bar")
 * ```
 *
 * @param text お知らせテキスト
 * @returns パース結果(DocumentFragment)
 */
export const parseNewsText = (text: string): DocumentFragment => {
    const fragment = document.createDocumentFragment();

    // [テキスト](URL) 形式と [URL] 形式の両方にマッチする正規表現
    // eslint-disable-next-line sonarjs/super-linear-regex
    const linkPattern = /\[([^\]]+)\]\(([^)]+)\)|\[([^\]]+)\]/gu;

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = linkPattern.exec(text)) !== null) {
        // マッチの前のテキストを追加
        if (match.index > lastIndex) {
            appendTextWithBreaks(fragment, text.slice(lastIndex, match.index));
        }

        // URL取得と消毒
        const rawUrl = match[2] ?? match[3];
        const sanitizedUrl = sanitizeUrl(rawUrl);

        if (sanitizedUrl) {
            // 安全なURLの場合のみリンク要素を作成
            const anchor = document.createElement("a");
            anchor.href = sanitizedUrl;
            anchor.target = "_blank";

            if (match[2] === undefined) {
                // [URL] 形式
                anchor.textContent = match[3];
            } else {
                // [テキスト](URL) 形式
                anchor.textContent = match[1];
            }

            fragment.append(anchor);
        } else {
            // 危険なURLの場合はテキストとして扱う
            appendTextWithBreaks(fragment, match[0]);
        }

        lastIndex = linkPattern.lastIndex;
    }

    // 残りのテキストを追加
    if (lastIndex < text.length) {
        appendTextWithBreaks(fragment, text.slice(lastIndex));
    }

    return fragment;
};
